import type { ExperimentConfig, ExperimentStep, ExperimentPart } from '@/types';

// ============================================================================
// EXPERIMENT 2: Qualitative Analysis Part I - Cations
// ============================================================================

// Color codes for cation analysis
export const cationColors = {
  // Cation solution colors
  cu2Initial: '#64B5F6',      // Light blue - hydrated copper(II)
  cu2Intermediate: '#90CAF9', // Cloudy light blue - Cu(OH)2 precipitate
  cu2Final: '#1565C0',        // Deep royal blue - [Cu(NH3)4]2+ complex
  agInitial: '#F5F5F5',       // Colorless
  agPrecipitate: '#FAFAFA',   // White - AgCl
  ni2Initial: '#A5D6A7',      // Pale green - hydrated nickel(II)
  ni2Basic: '#80CBC4',        // Blue-green - basic nickel
  ni2DMG: '#E53935',          // Bright red - nickel dimethylglyoximate
  al3Initial: '#F5F5F5',      // Colorless
  alAluminon: '#FF8A65',      // Pink-orange - before rinse
  alFinal: '#F44336',         // Bright red - aluminum-aluminon lake
  // Reagent colors
  nh4oh: '#F5F5F5',
  hcl: '#F5F5F5',
  dmg: '#FFFDE7',
  aluminon: '#FF7043',
  // UI colors
  filterPaper: '#FFF8E1',
  workbench: '#5D4037',
};

// Cation data
export interface CationData {
  id: string;
  symbol: string;
  name: string;
  charge: string;
  initialColor: string;
  confirmationReagent: string;
  positiveResult: string;
  positiveColor: string;
}

export const cationData: Record<string, CationData> = {
  cu: {
    id: 'cu',
    symbol: 'Cu2+',
    name: 'Copper(II)',
    charge: '2+',
    initialColor: '#64B5F6',
    confirmationReagent: 'NH4OH (excess)',
    positiveResult: 'Deep blue colour indicates presence of copper ions',
    positiveColor: '#1565C0',
  },
  ag: {
    id: 'ag',
    symbol: 'Ag+',
    name: 'Silver',
    charge: '+',
    initialColor: '#F5F5F5',
    confirmationReagent: '6M HCl',
    positiveResult: 'White precipitate of AgCl (silver chloride)',
    positiveColor: '#FAFAFA',
  },
  ni: {
    id: 'ni',
    symbol: 'Ni2+',
    name: 'Nickel(II)',
    charge: '2+',
    initialColor: '#A5D6A7',
    confirmationReagent: 'NH4OH + DMG',
    positiveResult: 'Red precipitate of nickel dimethylglyoximate',
    positiveColor: '#E53935',
  },
  al: {
    id: 'al',
    symbol: 'Al3+',
    name: 'Aluminum',
    charge: '3+',
    initialColor: '#F5F5F5',
    confirmationReagent: 'Aluminon (spot test)',
    positiveResult: 'Red spot remains after rinsing with distilled water',
    positiveColor: '#F44336',
  },
};

// Part 1: Introduction and Setup
const part1Steps: ExperimentStep[] = [
  {
    id: 1,
    title: 'Safety Check',
    shortDescription: 'Review safety procedures for cation analysis',
    handler: 'handleSafetyAcknowledgement',
    infoContent: {
      title: 'Lab Safety',
      content: `Safety precautions for cation analysis:
- Wear safety goggles at all times
- Handle acids (HCl, HNO3) with care - they are corrosive
- NH4OH has a strong ammonia odor - work in ventilation
- AgNO3 stains skin - avoid contact
- Wash hands thoroughly after the experiment`,
      whatHappens: 'Safety protocols are reviewed for handling corrosive acids, ammonia solutions, and silver nitrate before beginning cation analysis.',
      observe: [
        'Confirm safety goggles are worn and fit properly',
        'Identify the location of the fume hood for ammonia use',
        'Note that AgNO3 stains skin brown-black and should be handled carefully',
      ],
    },
  },
  {
    id: 2,
    title: 'Understand Cations',
    shortDescription: 'Learn about the four cations to be tested',
    handler: 'handleLearnCations',
    infoContent: {
      title: 'What is a Cation?',
      content: `A cation is a positively charged ion formed when an atom loses one or more electrons. Metals typically form cations because they have low ionization energies. For example:
- Cu loses 2 electrons to form Cu2+
- Ag loses 1 electron to form Ag+
- Ni loses 2 electrons to form Ni2+
- Al loses 3 electrons to form Al3+`,
      chemicalEquation: 'Cu -> Cu2+ + 2e-',
      whatHappens: 'The four metal cations are introduced: each forms by losing electrons, creating positively charged ions with characteristic solution colors.',
      observe: [
        'Cu2+ solution is light blue due to the hydrated copper(II) ion',
        'Ag+ and Al3+ solutions are colorless',
        'Ni2+ solution is pale green due to hydrated nickel(II) ions',
      ],
    },
  },
  {
    id: 3,
    title: 'Prepare Solutions',
    shortDescription: 'Set up labeled test tubes with cation solutions',
    handler: 'handlePrepareSolutions',
    validation: {
      requires: ['safetyAcknowledged'],
    },
  },
];

// Part 2: Copper Confirmation Test
const part2Steps: ExperimentStep[] = [
  {
    id: 1,
    title: 'Add Cu2+ Solution',
    shortDescription: 'Add 3 mL Cu2+ solution to a clean test tube',
    handler: 'handleAddCuSolution',
    infoContent: {
      title: 'Copper(II) Solution',
      content: `The light blue color of Cu2+ solution is due to the hydrated copper(II) ion [Cu(H2O)6]2+. The water molecules act as ligands, coordinating with the copper ion.`,
      whatHappens: 'A measured amount of Cu2+ solution is added to a clean test tube; its light blue color comes from the hexaaquacopper(II) complex [Cu(H2O)6]2+.',
      observe: [
        'The solution has a distinct light blue color',
        'The color is uniform throughout the solution',
        'Use a clean test tube to avoid contamination from other cations',
      ],
    },
  },
  {
    id: 2,
    title: 'Add Concentrated NH4OH Dropwise',
    shortDescription: 'Carefully add drops of concentrated ammonium hydroxide until basic',
    handler: 'handleAddNH4OHtoCu',
    validation: {
      requires: ['cuSolutionAdded'],
    },
    infoContent: {
      title: 'Testing for Basic pH',
      content: `Use litmus paper to test the pH. Continue adding NH4OH until red litmus paper turns blue, indicating a basic solution (pH > 7). This basic environment is essential for the complex formation.`,
      whatHappens: 'Concentrated ammonium hydroxide is added dropwise until the solution becomes basic, initially forming a light blue Cu(OH)2 precipitate before excess ammonia dissolves it.',
      observe: [
        'A cloudy light blue precipitate of Cu(OH)2 may form initially',
        'Test pH with red litmus paper after each addition using a stirring rod',
        'Continue adding NH4OH until red litmus turns blue (basic pH confirmed)',
      ],
    },
  },
  {
    id: 3,
    title: 'Test pH with Litmus',
    shortDescription: 'Confirm solution is basic with red litmus paper',
    handler: 'handleTestCupH',
    validation: {
      requires: ['nh4ohAddedToCu'],
    },
  },
  {
    id: 4,
    title: 'Observe Color Change',
    shortDescription: 'Observe the deep blue colour',
    handler: 'handleObserveCuColor',
    validation: {
      requires: ['cuIsBasic'],
    },
    infoContent: {
      title: 'The Copper-Ammonia Complex',
      content: `When excess ammonia (NH3) is added to Cu2+ solution, it forms the deep blue tetraaminecopper(II) complex [Cu(NH3)4]2+. Initially, Cu(OH)2 precipitates (light blue), but with excess ammonia, this dissolves to form the soluble complex.

The ammonia molecules act as ligands, donating electron pairs to the copper ion, forming coordinate covalent bonds.`,
      chemicalEquation: 'Cu2+ + 4NH3 -> [Cu(NH3)4]2+',
      whatHappens: 'Excess ammonia dissolves the Cu(OH)2 precipitate and forms the deep royal blue tetraaminecopper(II) complex [Cu(NH3)4]2+ through ligand exchange.',
      observe: [
        'The solution transforms from light blue to a striking deep royal blue color',
        'Any initial Cu(OH)2 precipitate dissolves as excess ammonia is added',
        'The deep blue color confirms the presence of Cu2+ ions',
      ],
    },
  },
  {
    id: 5,
    title: 'Record Observation',
    shortDescription: 'Record the positive copper confirmation',
    handler: 'handleRecordCuResult',
    validation: {
      requires: ['cuColorObserved'],
    },
  },
];

// Part 3: Silver Confirmation Test
const part3Steps: ExperimentStep[] = [
  {
    id: 1,
    title: 'Add Ag+ Solution',
    shortDescription: 'Add 3 mL Ag+ solution to a clean test tube',
    handler: 'handleAddAgSolution',
    infoContent: {
      title: 'Silver Ion Solution',
      content: `Ag+ solution is colorless. Silver ions are highly reactive with halide ions, particularly chloride, making this test very reliable.`,
      whatHappens: 'A measured amount of colorless Ag+ solution (silver nitrate) is placed in a clean test tube for the chloride precipitation test.',
      observe: [
        'The Ag+ solution is completely colorless and transparent',
        'Ensure the test tube is clean and free of contaminants',
        'Handle AgNO3 carefully as it stains skin and clothing',
      ],
    },
  },
  {
    id: 2,
    title: 'Add HCl',
    shortDescription: 'Add 2-3 drops of 6M HCl',
    handler: 'handleAddHCltoAg',
    validation: {
      requires: ['agSolutionAdded'],
    },
    infoContent: {
      title: 'Silver Chloride Precipitate',
      content: `Add **2-3 drops of 6M HCl** (hydrochloric acid) to the Ag solution. Silver ions react with chloride ions to form the highly insoluble silver chloride (AgCl). The presence of a precipitate AgCl (silver chloride) will confirm the presence of Ag⁺ ions.

Ksp of AgCl = 1.77 x 10^-10 (extremely insoluble)`,
      chemicalEquation: 'Ag+ + Cl- -> AgCl(s)',
      whatHappens: 'Chloride ions from HCl combine with Ag+ ions to form a white curdy precipitate of silver chloride (AgCl), which is extremely insoluble (Ksp = 1.77 x 10^-10).',
      observe: [
        'A white curdy precipitate of AgCl forms immediately upon adding HCl',
        'The precipitate settles and is clearly visible against the clear solution',
        'The instant formation of white precipitate confirms the presence of Ag+ ions',
      ],
    },
  },
  {
    id: 3,
    title: 'Observe Precipitate',
    shortDescription: 'Observe the white precipitate of AgCl',
    handler: 'handleObserveAgPrecipitate',
    validation: {
      requires: ['hclAddedToAg'],
    },
  },
  {
    id: 4,
    title: 'Record Observation',
    shortDescription: 'Record the positive silver confirmation',
    handler: 'handleRecordAgResult',
    validation: {
      requires: ['agPrecipitateObserved'],
    },
  },
];

// Part 4: Nickel Confirmation Test
const part4Steps: ExperimentStep[] = [
  {
    id: 1,
    title: 'Add Ni2+ Solution',
    shortDescription: 'Add 3 mL Ni2+ solution to a clean test tube',
    handler: 'handleAddNiSolution',
    infoContent: {
      title: 'Nickel(II) Solution',
      content: `The pale green color of Ni2+ solution is due to the hydrated nickel(II) ion [Ni(H2O)6]2+.`,
      whatHappens: 'A measured amount of pale green Ni2+ solution is placed in a clean test tube; the color arises from the hexaaquanickel(II) complex [Ni(H2O)6]2+.',
      observe: [
        'The solution has a characteristic pale green color',
        'The color is distinctly different from the blue Cu2+ solution',
        'Use a clean test tube to avoid cross-contamination',
      ],
    },
  },
  {
    id: 2,
    title: 'Add 6M NH4OH',
    shortDescription: 'Add enough 6M NH4OH (ammonium hydroxide) to basify',
    handler: 'handleAddNH4OHtoNi',
    validation: {
      requires: ['niSolutionAdded'],
    },
  },
  {
    id: 3,
    title: 'Test pH with Litmus',
    shortDescription: 'Confirm solution is basic',
    handler: 'handleTestNipH',
    validation: {
      requires: ['nh4ohAddedToNi'],
    },
  },
  {
    id: 4,
    title: 'Add DMG Solution',
    shortDescription: 'Add 3 drops of dimethylglyoxime (DMG) solution',
    handler: 'handleAddDMG',
    validation: {
      requires: ['niIsBasic'],
    },
    infoContent: {
      title: 'DMG Chelation - The Nickel Test',
      content: `Dimethylglyoxime (DMG) is a chelating agent that forms a characteristic bright red complex specifically with nickel ions. The complex is called nickel dimethylglyoximate.

The reaction is highly selective - other cations do not give this distinctive red precipitate, making it an excellent confirmation test for Ni2+.

The test must be performed in basic solution for optimal results.`,
      chemicalEquation: 'Ni2+ + 2DMG -> Ni(DMG)2 (red precipitate)',
      whatHappens: 'DMG chelates with Ni2+ ions in basic solution to form a bright red insoluble nickel dimethylglyoximate complex, which is highly specific to nickel.',
      observe: [
        'A bright red precipitate forms upon adding DMG drops to the basic Ni2+ solution',
        'The red color is distinctive and does not occur with other cations',
        'The precipitate may appear as red flakes or a red-colored solid',
      ],
    },
  },
  {
    id: 5,
    title: 'Stir Solution',
    shortDescription: 'Stir to mix the solution',
    handler: 'handleStirNiSolution',
    validation: {
      requires: ['dmgAdded'],
    },
  },
  {
    id: 6,
    title: 'Observe Red Precipitate',
    shortDescription: 'Observe the characteristic red precipitate',
    handler: 'handleObserveNiPrecipitate',
    validation: {
      requires: ['niStirred'],
    },
  },
  {
    id: 7,
    title: 'Record Observation',
    shortDescription: 'Record the positive nickel confirmation',
    handler: 'handleRecordNiResult',
    validation: {
      requires: ['niPrecipitateObserved'],
    },
  },
];

// Part 5: Aluminum Confirmation Test (Spot Test)
const part5Steps: ExperimentStep[] = [
  {
    id: 1,
    title: 'Add Al3+ Solution',
    shortDescription: 'Add 3 mL Al3+ solution to a test tube',
    handler: 'handleAddAlSolution',
    infoContent: {
      title: 'Aluminum Ion Solution',
      content: `Al3+ solution is colorless. The aluminon test is performed as a spot test on filter paper, which allows for a more sensitive detection.`,
      whatHappens: 'A measured amount of colorless Al3+ solution is prepared for the sensitive aluminon spot test on filter paper.',
      observe: [
        'The Al3+ solution is completely colorless',
        'Unlike Cu2+ and Ni2+, aluminum ions do not impart color to aqueous solutions',
        'Have filter paper and aluminon reagent ready for the spot test procedure',
      ],
    },
  },
  {
    id: 2,
    title: 'Adjust pH to ~6',
    shortDescription: 'Use HCl or NH4OH to adjust pH to approximately 6',
    handler: 'handleAdjustAlpH',
    validation: {
      requires: ['alSolutionAdded'],
    },
    infoContent: {
      title: 'pH Adjustment',
      content: `Test the solution with pH paper to ensure the solution is around pH 6. Add dropwise **6M HCl or 6M NH₄OH** to reach the appropriate pH.`,
      whatHappens: 'The Al3+ solution is adjusted to approximately pH 6 using HCl or NH4OH, which is optimal for the aluminon dye lake formation.',
      observe: [
        'Check pH paper color against the reference chart to confirm pH ~6',
        'Add acid or base dropwise to avoid overshooting the target pH',
        'The solution should remain colorless after pH adjustment',
      ],
    },
  },
  {
    id: 3,
    title: 'Place Drop on Filter Paper',
    shortDescription: 'Place one drop of solution on filter paper',
    handler: 'handlePlaceDropOnPaper',
    validation: {
      requires: ['alpHAdjusted'],
    },
  },
  {
    id: 4,
    title: 'Add Aluminon',
    shortDescription: 'Add one drop of aluminon solution',
    handler: 'handleAddAluminon',
    validation: {
      requires: ['dropOnPaper'],
    },
    infoContent: {
      title: 'The Aluminon Spot Test',
      content: `**Aluminon** is the triammonium salt of aurintricarboxylic acid, a dye commonly used to detect aluminum ions in aqueous solution. It forms a red "lake" with aluminum ions — a pigment formed when a dye is adsorbed onto an inorganic compound.

The test is performed on filter paper at pH ~6. After adding the solution and aluminon, wait **30 seconds**, then rinse the paper with water.
- **If aluminum is PRESENT:** A bright red spot remains after rinsing
- **If aluminum is ABSENT:** The color washes away completely

This is a highly sensitive and specific test for aluminum.`,
      chemicalEquation: 'Al3+ + Aluminon -> Al-Aluminon lake (red)',
      whatHappens: 'Aluminon dye adsorbs onto an aluminum hydroxide matrix to form a bright red "lake" pigment on the filter paper that resists washing with water.',
      observe: [
        'An orange-pink spot forms where aluminon contacts the Al3+ solution on filter paper',
        'After rinsing, a bright red spot persists if aluminum is present',
        'If aluminum is absent, the color washes away completely',
      ],
    },
  },
  {
    id: 5,
    title: 'Wait 30 Seconds',
    shortDescription: 'Allow 30 seconds for the reaction',
    handler: 'handleWaitForAluminon',
    validation: {
      requires: ['aluminonAdded'],
    },
  },
  {
    id: 6,
    title: 'Rinse with Water',
    shortDescription: 'Rinse the filter paper with distilled water',
    handler: 'handleRinseFilterPaper',
    validation: {
      requires: ['aluminonWaited'],
    },
  },
  {
    id: 7,
    title: 'Observe Spot Color',
    shortDescription: 'Check if the red spot remains after rinsing',
    handler: 'handleObserveAlSpot',
    validation: {
      requires: ['paperRinsed'],
    },
  },
  {
    id: 8,
    title: 'Record Observation',
    shortDescription: 'Record the positive aluminum confirmation',
    handler: 'handleRecordAlResult',
    validation: {
      requires: ['alSpotObserved'],
    },
  },
];

// Part 6: Unknown Identification
const part6Steps: ExperimentStep[] = [
  {
    id: 1,
    title: 'Receive Unknown',
    shortDescription: 'Obtain your unknown cation solution',
    handler: 'handleReceiveUnknown',
    infoContent: {
      title: 'Unknown Cation Identification',
      content: `Your unknown may contain 1 or 2 cations from: Al3+, Ag+, Cu2+, Ni2+. Use the confirmation tests you learned to systematically identify all cations present.`,
      whatHappens: 'An unknown solution containing one or two cations is provided for systematic identification using the confirmation tests learned in previous parts.',
      observe: [
        'Note the initial color of the unknown solution (may hint at which cations are present)',
        'A blue-tinted solution suggests Cu2+; green suggests Ni2+; colorless could be Ag+ or Al3+',
        'Plan to test for all four cations systematically',
      ],
    },
  },
  {
    id: 2,
    title: 'Perform Cu2+ Test',
    shortDescription: 'Test for copper using NH4OH',
    handler: 'handleTestUnknownCu',
    validation: {
      requires: ['unknownReceived'],
    },
  },
  {
    id: 3,
    title: 'Perform Ag+ Test',
    shortDescription: 'Test for silver using HCl',
    handler: 'handleTestUnknownAg',
    validation: {
      requires: ['unknownReceived'],
    },
  },
  {
    id: 4,
    title: 'Perform Ni2+ Test',
    shortDescription: 'Test for nickel using NH4OH + DMG',
    handler: 'handleTestUnknownNi',
    validation: {
      requires: ['unknownReceived'],
    },
  },
  {
    id: 5,
    title: 'Perform Al3+ Test',
    shortDescription: 'Test for aluminum using aluminon spot test',
    handler: 'handleTestUnknownAl',
    validation: {
      requires: ['unknownReceived'],
    },
  },
  {
    id: 6,
    title: 'Submit Identification',
    shortDescription: 'Submit your identification of the unknown cation(s)',
    handler: 'handleSubmitIdentification',
    validation: {
      requires: ['allUnknownTestsComplete'],
    },
  },
];

// Initial States
const part1InitialState = {
  safetyAcknowledged: false,
  cationsLearned: false,
  solutionsPrepared: false,
};

const part2InitialState = {
  cuSolutionAdded: false,
  nh4ohAddedToCu: false,
  cuIsBasic: false,
  cuColorObserved: false,
  cuResult: null as 'positive' | 'negative' | null,
  cuSolutionColor: '#64B5F6',
};

const part3InitialState = {
  agSolutionAdded: false,
  hclAddedToAg: false,
  agPrecipitateObserved: false,
  agResult: null as 'positive' | 'negative' | null,
  agHasPrecipitate: false,
};

const part4InitialState = {
  niSolutionAdded: false,
  nh4ohAddedToNi: false,
  niIsBasic: false,
  dmgAdded: false,
  niStirred: false,
  niPrecipitateObserved: false,
  niResult: null as 'positive' | 'negative' | null,
  niSolutionColor: '#A5D6A7',
};

const part5InitialState = {
  alSolutionAdded: false,
  alpHAdjusted: false,
  dropOnPaper: false,
  aluminonAdded: false,
  aluminonWaited: false,
  paperRinsed: false,
  alSpotObserved: false,
  alResult: null as 'positive' | 'negative' | null,
  spotColor: '#F5F5F5',
};

const part6InitialState = {
  unknownReceived: false,
  unknownCations: [] as string[],
  unknownTests: {
    cu: null as 'positive' | 'negative' | null,
    ag: null as 'positive' | 'negative' | null,
    ni: null as 'positive' | 'negative' | null,
    al: null as 'positive' | 'negative' | null,
  },
  allUnknownTestsComplete: false,
  submittedIdentifications: [] as string[],
  isCorrect: null as boolean | null,
};

// Part Definitions
const part1: ExperimentPart = {
  id: 1,
  name: 'setup',
  title: 'Part I: Introduction & Setup',
  steps: part1Steps,
  equipment: ['Test tube rack', 'Test tubes', 'Labeled cation solutions'],
  initialState: part1InitialState,
};

const part2: ExperimentPart = {
  id: 2,
  name: 'copper',
  title: 'Part II: Copper (Cu2+) Confirmation',
  steps: part2Steps,
  equipment: ['Cu2+ solution', 'Concentrated NH4OH', 'Litmus paper', 'Stirring rod'],
  initialState: part2InitialState,
};

const part3: ExperimentPart = {
  id: 3,
  name: 'silver',
  title: 'Part III: Silver (Ag+) Confirmation',
  steps: part3Steps,
  equipment: ['Ag+ solution', '6M HCl'],
  initialState: part3InitialState,
};

const part4: ExperimentPart = {
  id: 4,
  name: 'nickel',
  title: 'Part IV: Nickel (Ni2+) Confirmation',
  steps: part4Steps,
  equipment: ['Ni2+ solution', '6M NH4OH', 'DMG solution', 'Litmus paper', 'Stirring rod'],
  initialState: part4InitialState,
};

const part5: ExperimentPart = {
  id: 5,
  name: 'aluminum',
  title: 'Part V: Aluminum (Al3+) Confirmation',
  steps: part5Steps,
  equipment: ['Al3+ solution', 'pH paper', '6M HCl', '6M NH4OH', 'Filter paper', 'Aluminon solution', 'Wash bottle'],
  initialState: part5InitialState,
};

const part6: ExperimentPart = {
  id: 6,
  name: 'unknown',
  title: 'Part VI: Unknown Cation Identification',
  steps: part6Steps,
  equipment: ['Unknown solution', 'All confirmation reagents'],
  initialState: part6InitialState,
};

// ============================================================================
// FULL EXPERIMENT CONFIGURATION
// ============================================================================

const experiment2Config: ExperimentConfig = {
  id: 'exp2',
  number: 2,
  name: 'Qualitative Analysis Part I - Cations',
  description: 'Identify metal cations (Cu2+, Ag+, Ni2+, Al3+) through confirmation tests using specific reagents, observing color changes, precipitate formation, and spot tests.',
  estimatedTime: '90 minutes',
  learningObjectives: [
    'Understand the concept of cations and how they form',
    'Learn specific confirmation tests for Cu2+, Ag+, Ni2+, and Al3+',
    'Observe characteristic color changes and precipitate formation',
    'Understand the chemistry of complex ion formation',
    'Apply systematic testing to identify unknown cations',
    'Use pH control to optimize chemical reactions',
  ],
  safetyNotes: [
    'Wear safety goggles at all times',
    'Good housekeeping is essential - clean up any spilled materials in or near the balance immediately',
    'Handle sample material with a clean spatula only - never return anything to the bottle',
    'Handle HCl and HNO3 with care - they are corrosive',
    'NH4OH has a strong ammonia odor - work in ventilation',
    'AgNO3 stains skin and clothing - avoid contact',
    'Wash hands thoroughly after the experiment',
    'Disposal: Ammonium hydroxide & hydrochloric acid; metals have their own waste containers',
  ],
  requiredEquipment: [
    'Test tube rack',
    'Test tubes',
    'Watch glass',
    'Cu²⁺ solution',
    'Ag⁺ solution (0.1 M AgNO₃)',
    'Ni²⁺ solution',
    'Al³⁺ solution',
    'Concentrated NH₄OH',
    '6 M NH₄OH',
    '6 M HCl',
    'DMG (dimethylglyoxime) solution',
    'Aluminon solution (triammonium salt of aurintricarboxylic acid)',
    'Litmus paper (red and blue)',
    'pH paper',
    'Filter paper',
    'Wash bottle (deionized water)',
    'Stirring rod',
  ],
  parts: [part1, part2, part3, part4, part5, part6],
  quiz: {
    experimentId: 'exp2',
    title: 'Cation Analysis Quiz',
    questions: [
      {
        id: 'exp2-q1',
        type: 'mcq' as const,
        question: 'When excess NH₄OH is added to a solution containing Cu\u00B2⁺, what is the color of the resulting complex ion?',
        options: [
          { id: 'a', text: 'Pale green' },
          { id: 'b', text: 'Deep royal blue' },
          { id: 'c', text: 'Bright red' },
          { id: 'd', text: 'Yellow' },
        ],
        correctOptionId: 'b',
        explanation: 'Excess ammonia reacts with Cu\u00B2⁺ to form the tetraamminecopper(II) complex [Cu(NH₃)₄]\u00B2⁺, which has a characteristic deep royal blue color. This is the confirmation test for copper(II).',
      },
      {
        id: 'exp2-q2',
        type: 'mcq' as const,
        question: 'Which reagent is used as the specific confirmation test for Ni\u00B2⁺?',
        options: [
          { id: 'a', text: 'Aluminon solution' },
          { id: 'b', text: 'Excess NH₄OH' },
          { id: 'c', text: 'DMG (dimethylglyoxime)' },
          { id: 'd', text: '6 M HCl' },
        ],
        correctOptionId: 'c',
        explanation: 'Dimethylglyoxime (DMG) forms a bright red precipitate (nickel dimethylglyoximate) with Ni\u00B2⁺ in slightly basic conditions. This is a highly specific and sensitive test for nickel.',
      },
      {
        id: 'exp2-q3',
        type: 'mcq' as const,
        question: 'What happens when HCl is added to a solution containing Ag⁺?',
        options: [
          { id: 'a', text: 'A deep blue solution forms' },
          { id: 'b', text: 'A white curdy precipitate of AgCl forms' },
          { id: 'c', text: 'A bright red precipitate forms' },
          { id: 'd', text: 'No visible reaction occurs' },
        ],
        correctOptionId: 'b',
        explanation: 'Silver ions react with chloride ions to form silver chloride (AgCl), which is an insoluble white curdy precipitate: Ag⁺ + Cl⁻ → AgCl\u2193.',
      },
      {
        id: 'exp2-q4',
        type: 'short_answer' as const,
        question: 'Explain why the aluminon test for Al\u00B3⁺ requires the solution to be made basic with NH₄OH and why the precipitate must be washed with hot water.',
        sampleAnswer: 'The aluminon test requires basic conditions because aluminum hydroxide Al(OH)₃ must first precipitate, and the aluminon dye adsorbs onto this precipitate to form a red "lake." Washing with hot water removes excess (unadsorbed) aluminon dye from the precipitate, so only the red aluminum-aluminon lake remains. Without washing, the orange color of excess aluminon could give a false positive.',
      },
      {
        id: 'exp2-q5',
        type: 'calculation' as const,
        question: 'If 10.0 mL of 0.10 M AgNO₃ is mixed with excess HCl, what mass of AgCl precipitate (molar mass = 143.32 g/mol) would theoretically form?',
        expectedValue: 0.143,
        tolerance: 2,
        unit: 'g',
      },
    ],
  },
};

export default experiment2Config;
